// src/components/settings/ChangePassword.tsx
//
// Settings → Change Password. Session-authenticated password set/change via
// supabase.auth.updateUser — works both for a normal signed-in user and for
// the invite-link session an admin-created user lands in (Story 1.4's invite
// flow redirects here so the new user can set a password). Deliberately
// minimal: new password + confirm, inline success/error, pending state.

import React, { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'

const MIN_LENGTH = 8

export function ChangePassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (pending) return
    setError(null)

    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`)
      return
    }
    if (password !== confirm) {
      setError('The two passwords don’t match.')
      return
    }

    setPending(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      })
      if (updateError) throw new Error(updateError.message)
      setDone(true)
      setPassword('')
      setConfirm('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update password')
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Change Password</h2>
      <p className="text-sm text-gray-600 mb-4">
        Set a new password for your account.
      </p>

      {done && (
        <div
          role="status"
          className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm"
        >
          Your password has been updated.
        </div>
      )}
      {error && (
        <div
          id="change-password-error"
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}

      <form onSubmit={submit} className="space-y-4 max-w-sm">
        <Input
          type="password"
          label="New password"
          autoComplete="new-password"
          value={password}
          aria-describedby={error ? 'change-password-error' : undefined}
          onChange={e => {
            setPassword(e.target.value)
            setDone(false)
          }}
        />
        <Input
          type="password"
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          aria-describedby={error ? 'change-password-error' : undefined}
          onChange={e => {
            setConfirm(e.target.value)
            setDone(false)
          }}
        />
        <Button
          type="submit"
          loading={pending}
          disabled={!password || !confirm}
        >
          Update password
        </Button>
      </form>
    </div>
  )
}
